import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { RefreshCw } from "lucide-react";
import { SYNC_REASON_COPY, getRemoteLockErrorMessage, getRemoteLockPropertySync, runRemoteLockPropertySync } from "../../utils/remotelock";

const addressLine = (a) => (a ? [a.line1, a.line2, a.city, a.state, a.zipCode].filter(Boolean).join(", ") : "No address in RemoteLock");
const itemTitle = (item) => (item.unitName ? `${item.locationName || "Location"} — ${item.unitName}` : item.locationName || "Location");

function Count({ label, value }) {
  return (
    <div className="rounded-xl border border-[#EAEAEA] bg-white px-3 py-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#888888]">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-[#111111]">{value}</p>
    </div>
  );
}

/**
 * Read-only status of the automatic RemoteLock → Bellhopt property import.
 * Every location/unit is imported automatically once the host authorizes
 * RemoteLock; there is no per-property approval. Items that could not be
 * imported are listed with the reason (fixable only in RemoteLock).
 */
export default function RemoteLockPropertySyncPanel({ hid }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!hid) return;
    try {
      setState(await getRemoteLockPropertySync(hid));
    } catch (error) {
      setState(null);
    }
  }, [hid]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRun = async () => {
    setBusy(true);
    try {
      await runRemoteLockPropertySync(hid);
      toast.success("Property sync finished");
      await load();
    } catch (error) {
      toast.error(getRemoteLockErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const pending = useMemo(() => (state?.links || []).filter((l) => l.status === "skipped" || l.status === "review"), [state]);
  if (!state || !state.connected) return null;
  const counts = state.counts || {};

  return (
    <div className="rounded-2xl border border-[#EAEAEA] bg-[#F7F7F8] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-bold text-[#111111]">Properties from RemoteLock</h4>
          <p className="mt-0.5 text-xs text-[#5F6368]">
            Every location and unit in your RemoteLock account is imported automatically and matched to your existing Bellhopt properties. New ones are picked up daily.
            {state.lastRunAt && <> Last run {new Date(state.lastRunAt).toLocaleString()}.</>}
          </p>
        </div>
        <button
          type="button"
          disabled={busy || state.running}
          onClick={handleRun}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={13} className={busy || state.running ? "animate-spin" : ""} />
          {state.running ? "Syncing…" : "Sync now"}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <Count label="Created" value={counts.created || 0} />
        <Count label="Linked to existing" value={counts.linked || 0} />
        <Count label="Not imported" value={pending.length} />
      </div>

      {state.lastError && <p className="mt-2 text-xs text-red-700">Last sync error: {state.lastError}</p>}

      {pending.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-[#5F6368]">Not imported yet ({pending.length}) — fix in RemoteLock and sync again</summary>
          <ul className="mt-2 space-y-2">
            {pending.map((item) => (
              <li key={item.id} className="rounded-xl border border-[#EAEAEA] bg-white p-3">
                <p className="text-sm font-semibold text-[#111111]">{itemTitle(item)}</p>
                <p className="mt-0.5 text-xs text-[#5F6368]">{addressLine(item.address)}</p>
                <p className="mt-1 text-xs text-amber-800">{SYNC_REASON_COPY[item.reason] || "Bellhopt support is reviewing this one."}</p>
              </li>
            ))}
          </ul>
        </details>
      )}

      {(counts.created || 0) > 0 && (
        <p className="mt-3 text-xs text-[#5F6368]">
          Imported properties need entry instructions and a mapped door before deliveries can use them.{" "}
          <Link to="/properties" className="font-medium text-[#111111] hover:underline">
            Review properties
          </Link>
        </p>
      )}
    </div>
  );
}
